import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Highcharts from 'highcharts';

import { binEndFromTs } from '../common/slotsUtils';
import { HH_MM_FORMAT } from '../common/utils';
import {
  axis_color,
  baseChartOptions,
  font_size,
  freeflow_color,
  getBandMembership,
  graph_highlight_color,
  grid_color,
  label_color,
  recent_speed_color,
} from './chartOptions';

dayjs.extend(utc);

const capacity_threshold_color = freeflow_color; // green
const typical_color = recent_speed_color;
const TICK_INTERVAL_HOUR = 1000 * 60 * 60; // milliseconds * seconds * minutes

// highcharts-react npm page
// https://www.npmjs.com/package/highcharts-react-official

// highcharts-react github repo
// https://github.com/highcharts/highcharts-react#readme

// highcharts documentation
// https://api.highcharts.com/highcharts/

export function getDayForTs(uses_middle_east_day_types, value) {
  let days = ['Mon-Fri', 'Saturday', 'Sunday'];
  if (uses_middle_east_day_types) {
    days = ['Sun-Thr', 'Friday', 'Saturday'];
  }
  const day_n = Math.min(
    Math.floor((value as number) / (TICK_INTERVAL_HOUR * 24)),
    days.length - 1,
  );
  return days[day_n];
}

export function getFormattedTime(ts) {
  return dayjs(ts).utc().format(HH_MM_FORMAT);
}

export function makeFormatTooltip(
  bin_width_mins,
  bands,
  uses_middle_east_day_types: boolean,
) {
  function formatTooltip(
    this: Highcharts.TooltipFormatterContextObject,
    tooltip: Highcharts.Tooltip,
  ) {
    const ts = this.x;
    const date = getFormattedTime(ts);
    const band = getBandMembership(ts, bands);
    const end_time = getFormattedTime(
      binEndFromTs(ts as number, bin_width_mins),
    );
    function safeToLocaleString(num) {
      if (num !== undefined && num !== null && Number.isFinite(num)) {
        return num.toLocaleString();
      } else {
        return 'Unknown';
      }
    }

    const tooltipLines = [];

    // Highlight band
    if (band) {
      // Justifying FF0E: https://kschaul.com/til/2022/09/13/coloring-emoji-unicode-characters-on-ios-safari/
      tooltipLines.push({
        name: `<span class="highlight-icon" style="color:${graph_highlight_color}">◼&#xFE0E;</span>Highlight`,
        value: `${getFormattedTime(band.raw_from)} - ${getFormattedTime(
          band.raw_to,
        )}`,
      });
    }
    // The capacity threshold flatline series has data points at x=0 and x=lastBin only.
    // this.points[0] becomes the capacity threshold at those two
    const metricPoint = this.points[this.points.length - 1];

    // Metric
    tooltipLines.push({
      name: `<span style="color:${metricPoint.series.color}">●</span>${metricPoint.series.name}`,
      value: safeToLocaleString(metricPoint?.y),
    });

    // Total day sum metric
    const daySums = (metricPoint?.series as any)?.userOptions?.data
      // Assign days
      ?.map((row) => {
        return {
          ...row,
          day: getDayForTs(uses_middle_east_day_types, row.x),
        };
      })
      // Group by day and sum
      .reduce((acc, curr) => {
        if (!acc[curr.day]) {
          acc[curr.day] = 0;
        }
        acc[curr.day] += curr.y;
        return acc;
      }, {});

    const day = getDayForTs(uses_middle_east_day_types, metricPoint?.x);
    tooltipLines.push({
      name: `Total Daily`,
      value: safeToLocaleString(daySums[day] * (bin_width_mins / 60)),
    });

    return `
    <div class="chart-legend" style="font-size: 15px;">
      <table>
        <h1>${date} - ${end_time} ${day}</h1>
        ${tooltipLines.map((tooltipLine) => {
          return `
            <tr>
              <td>
                ${tooltipLine.name}
              </td>
              <td>
                <b>${tooltipLine.value}</b>
              </td>
              </tr>`;
        })}
      </table>
    </div>`;
  }
  return formatTooltip;
}

export function makeVolumeAxisLabeller(uses_middle_east_day_types: boolean) {
  return function volumeAxisLabelFormatter(
    this: Highcharts.AxisLabelsFormatterContextObject,
  ) {
    let label = this.axis.defaultLabelFormatter.call(this);
    const day = getDayForTs(uses_middle_east_day_types, this.value);
    if (label === '12:00') {
      // Add the day on a new line when the label is mid day
      label += `<br/>${day}`;
    }
    return label;
  };
}

export const planningChartOptions = {
  ...baseChartOptions,
  time: {
    useUTC: true,
  },
  chart: {
    ...baseChartOptions.chart,
    marginRight: '170',
  },
  yAxis: {
    min: 0,
    lineColor: axis_color,
    gridLineColor: grid_color,
    gridLineWidth: 1,
    lineWidth: 1,
    tickWidth: 1,
    title: {
      text: 'Volume',
      style: {
        color: label_color,
        fontSize: font_size,
      },
    },
    labels: {
      style: {
        color: label_color,
        fontSize: font_size,
      },
    },
  },
  xAxis: {
    type: 'datetime',
    dateTimeLabelFormats: {
      day: '%H:%M',
    },
    minPadding: 0,
    maxPadding: 0,
    // startOnTick: true,
    endOnTick: true,
    lineColor: axis_color,
    gridLineColor: grid_color, // vertical lines
    minorGridLineColor: grid_color,
    gridLineWidth: 1,
    tickPixelInterval: 50,
    tickInterval: TICK_INTERVAL_HOUR * 6,
    minorTickInterval: TICK_INTERVAL_HOUR,
    labels: {
      style: {
        color: label_color,
        fontSize: font_size,
      },
      rotation: 0,
    },
    plotLines: [
      {
        width: 2,
        value: TICK_INTERVAL_HOUR * 24,
      },
      {
        width: 2,
        value: 2 * TICK_INTERVAL_HOUR * 24,
      },
    ],
  },
  series: [
    {
      id: 'capacity_threshold_flatline',
      data: [],
      name: 'Capacity',
      color: capacity_threshold_color,
      lineWidth: 2,
      marker: { enabled: false },
    },
    {
      id: 'typical_volume',
      type: 'spline',
      data: [],
      name: 'Typical Volume',
      color: typical_color,
      lineWidth: 2,
      marker: { enabled: false, symbol: 'circle' },
    },
  ],
  legend: {
    ...baseChartOptions.legend,
    itemWidth: 150,
  },
};
